/**
 * Notifications (tracker item 62), /notifications: the whole list of the
 * signed-in office user's alerts, newest first, with an All / Unread filter,
 * "Load more", mark read per row and "Mark all read". Clicking an alert marks
 * it read and opens its record. ADMIN / OFFICE_MANAGER only (no request is made
 * for anybody else).
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { createAPIClient, IStaffNotificationsApi } from '../../api';
import * as types from '../../api/types';
import { useAuth } from '../../context/AuthContext';
import { canViewStaffNotifications } from '../../utils/permissions';
import { getErrorMessage } from '../../utils/apiError';
import { STAFF_ALERT_PREFERENCES_PATH, notifyStaffNotificationsChanged, staffNotificationPath, timeAgo } from '../../utils/staffNotifications';
import { secondaryButtonClass } from '../../components/formStyles';
import { Toast } from '../../components/Toast';

const PAGE_SIZE = 20;
const tap = 'min-h-[44px] sm:min-h-0';

export function StaffNotificationsPage() {
  const { user } = useAuth();
  const api = useMemo<IStaffNotificationsApi>(() => createAPIClient(), []);
  const navigate = useNavigate();
  const allowed = canViewStaffNotifications(user);
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [items, setItems] = useState<types.StaffNotification[]>([]);
  const [total, setTotal] = useState(0);
  const [unread, setUnread] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [loadedAt, setLoadedAt] = useState<string | null>(null);

  const load = useCallback(
    async (target: number, replace: boolean) => {
      setLoading(true);
      try {
        const result = await api.listStaffNotifications({ unread: unreadOnly, page: target, limit: PAGE_SIZE });
        setItems((prev) => (replace ? result.data : [...prev, ...result.data.filter((n) => !prev.some((p) => p.id === n.id))]));
        setTotal(result.total);
        setUnread(result.unread);
        setPage(target);
        if (replace) setLoadedAt(new Date().toISOString());
        setError(null);
      } catch (err) {
        setError(getErrorMessage(err, 'Could not load notifications'));
      } finally {
        setLoading(false);
      }
    },
    [api, unreadOnly]
  );

  useEffect(() => {
    if (!allowed) return;
    void load(1, true);
  }, [allowed, load]);

  if (!allowed) {
    return <p className="p-4 text-sm text-gray-600">Notifications are available to admins and office managers.</p>;
  }

  const markRead = async (item: types.StaffNotification) => {
    try {
      await api.markStaffNotificationRead(item.id);
      const now = new Date().toISOString();
      setItems((prev) => (unreadOnly ? prev.filter((n) => n.id !== item.id) : prev.map((n) => (n.id === item.id ? { ...n, readAt: n.readAt ?? now } : n))));
      if (unreadOnly) setTotal((t) => Math.max(0, t - 1));
      setUnread((u) => (item.readAt ? u : Math.max(0, u - 1)));
      notifyStaffNotificationsChanged();
    } catch (err) {
      setError(getErrorMessage(err, 'Could not mark the notification read'));
    }
  };

  const open = async (item: types.StaffNotification) => {
    const path = staffNotificationPath(item.link);
    if (!item.readAt) await markRead(item);
    if (path) navigate(path);
  };

  const markAll = async () => {
    try {
      await api.markAllStaffNotificationsRead(loadedAt ? { before: loadedAt } : {});
      notifyStaffNotificationsChanged();
      await load(1, true);
    } catch (err) {
      setError(getErrorMessage(err, 'Could not mark notifications read'));
    }
  };

  const hasMore = items.length < total;

  return (
    <main className="p-4 sm:p-6 max-w-3xl space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Notifications</h1>
          <p className="mt-1 text-sm text-gray-600">
            Alerts for your team: new requests, customer replies, payments, estimate answers and finished jobs.{' '}
            <Link to={STAFF_ALERT_PREFERENCES_PATH} className="text-blue-600 hover:underline">Choose which alerts you get</Link>.
          </p>
        </div>
        <button type="button" onClick={() => void markAll()} disabled={unread === 0 && !items.some((i) => !i.readAt)} className={`${secondaryButtonClass} ${tap}`}>
          Mark all read
        </button>
      </div>

      <div role="group" aria-label="Show" className="inline-flex rounded-md border border-gray-300 bg-white p-0.5 text-sm">
        {[
          { label: 'All', value: false },
          { label: unread > 0 ? `Unread (${unread > 99 ? '99+' : unread})` : 'Unread', value: true },
        ].map((option) => (
          <button
            key={option.label}
            type="button"
            aria-pressed={unreadOnly === option.value}
            onClick={() => setUnreadOnly(option.value)}
            className={`${tap} rounded px-3 py-1.5 font-medium ${unreadOnly === option.value ? 'bg-blue-600 text-white' : 'text-gray-700 hover:bg-gray-100'}`}
          >
            {option.label}
          </button>
        ))}
      </div>

      {error && <Toast kind="error">{error}</Toast>}
      {!loading && items.length === 0 && !error && (
        <p className="rounded-md border border-gray-200 bg-white p-4 text-sm text-gray-600">
          {unreadOnly ? 'No unread notifications.' : 'No notifications yet.'}
        </p>
      )}
      {items.length > 0 && (
        <ul className="divide-y divide-gray-100 rounded-md border border-gray-200 bg-white">
          {items.map((item) => {
            const path = staffNotificationPath(item.link);
            return (
              <li key={item.id} data-unread={item.readAt ? 'false' : 'true'} className={`flex items-start gap-3 px-3 py-3 ${item.readAt ? '' : 'bg-blue-50/60'}`}>
                {!item.readAt && <span aria-label="Unread" className="mt-2 h-2 w-2 shrink-0 rounded-full bg-blue-600" />}
                <div className="min-w-0 flex-1">
                  {path ? (
                    <button type="button" onClick={() => void open(item)} className={`${tap} block max-w-full break-words text-left text-sm text-blue-700 hover:underline ${item.readAt ? '' : 'font-semibold'}`}>
                      {item.title}
                    </button>
                  ) : (
                    <p className={`break-words text-sm text-gray-900 ${item.readAt ? '' : 'font-semibold'}`}>{item.title}</p>
                  )}
                  {item.body && <p className="break-words text-sm text-gray-600">{item.body}</p>}
                  <p className="text-xs text-gray-500">{timeAgo(item.createdAt)}</p>
                </div>
                {!item.readAt && (
                  <button type="button" onClick={() => void markRead(item)} aria-label={`Mark read: ${item.title}`} className={`${tap} shrink-0 text-xs font-semibold text-blue-600 hover:text-blue-700`}>
                    Mark read
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}
      {loading && <p className="text-sm text-gray-500">Loading...</p>}
      {hasMore && !loading && (
        <button type="button" onClick={() => void load(page + 1, false)} className={`${secondaryButtonClass} ${tap}`}>
          Load more
        </button>
      )}
    </main>
  );
}
